import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useRecordPayment, useMpesaSTKPush } from '../../hooks/payments/usePayments';
import { PaymentRecordRequest } from '../../api/types/payment.types';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../hooks/auth/useAuth';
import { useLoans } from '../../hooks/loans/useLoans';
import { Loan, LoanStatus } from '../../api/types/loan.types';

const Payments: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [paymentMethod, setPaymentMethod] = useState<string>('MPESA');
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);

  const recordPaymentMutation = useRecordPayment();
  const mpesaSTKPushMutation = useMpesaSTKPush();

  // Fetch loans with outstanding balances (ACTIVE and OVERDUE)
  const { data: activeLoans = [], isLoading: isLoadingActive } = useLoans({ status: LoanStatus.ACTIVE });
  const { data: overdueLoans = [], isLoading: isLoadingOverdue } = useLoans({ status: LoanStatus.OVERDUE });

  // Combine and filter loans with outstanding balance
  const outstandingLoans = useMemo(() => {
    const allLoans = [...activeLoans, ...overdueLoans];
    return allLoans.filter(loan => loan.amountOutstanding > 0);
  }, [activeLoans, overdueLoans]);

  const isLoadingLoans = isLoadingActive || isLoadingOverdue;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PaymentRecordRequest>();

  const onSubmit = async (data: PaymentRecordRequest) => {
    try {
      if (data.paymentMethod === 'MPESA' && paymentMethod === 'MPESA') {
        // Initiate M-Pesa STK Push
        const stkResult = await mpesaSTKPushMutation.mutateAsync({
          loanId: data.loanId,
          customerId: selectedLoan?.customerId || '',
          phoneNumber: data.phoneNumber,
          amount: String(data.amount),
        });

        if (stkResult.success) {
          enqueueSnackbar('M-Pesa payment request sent!', { variant: 'success' });
        }
      } else {
        // Record manual payment
        await recordPaymentMutation.mutateAsync({
          ...data,
          recordedBy: user?.userId || '',
        });
        enqueueSnackbar('Payment recorded successfully!', { variant: 'success' });
        reset();
        setSelectedLoan(null);
      }
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Payment failed',
        { variant: 'error' }
      );
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Record Payment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Record loan repayments via M-Pesa, cash, or bank transfer
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 600 }}>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="loanId"
                control={control}
                rules={{ required: 'Please select a loan' }}
                render={({ field }) => (
                  <Autocomplete
                    options={outstandingLoans}
                    loading={isLoadingLoans}
                    value={selectedLoan}
                    onChange={(_, newValue) => {
                      setSelectedLoan(newValue);
                      field.onChange(newValue?.loanId || '');
                    }}
                    getOptionLabel={(option) =>
                      `${option.loanNumber} - ${option.customerName || 'Unknown'} (KES ${option.amountOutstanding.toLocaleString()} outstanding)`
                    }
                    isOptionEqualToValue={(option, value) => option.loanId === value.loanId}
                    renderOption={(props, option) => (
                      <li {...props} key={option.loanId}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {option.loanNumber} - {option.customerName || 'Unknown Customer'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Outstanding: KES {option.amountOutstanding.toLocaleString()} |
                            Status: {option.loanStatus} |
                            Due: {new Date(option.dueDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Loan"
                        required
                        error={!!errors.loanId}
                        helperText={errors.loanId?.message || 'Select a loan with outstanding balance'}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingLoans ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={isLoadingLoans ? 'Loading loans...' : 'No loans with outstanding balance'}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="amount"
                control={control}
                rules={{
                  required: 'Amount is required',
                  min: { value: 1, message: 'Amount must be positive' },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Amount (KES)"
                    type="number"
                    required
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="paymentMethod"
                control={control}
                defaultValue={'MPESA' as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Payment Method"
                    select
                    required
                    fullWidth
                    onChange={(e) => {
                      field.onChange(e);
                      setPaymentMethod(e.target.value as any); // eslint-disable-line @typescript-eslint/no-explicit-any
                    }}
                  >
                    <MenuItem value="MPESA">M-Pesa</MenuItem>
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                    <MenuItem value="CHEQUE">Cheque</MenuItem>
                  </TextField>
                )}
              />

              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: 'Phone number is required',
                  pattern: {
                    value: /^254[17]\d{8}$/,
                    message: 'Must start with 254 and be 12 digits',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone Number"
                    required
                    placeholder="254712345678"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="paymentReference"
                control={control}
                rules={{ required: 'Reference is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Payment Reference"
                    required
                    error={!!errors.paymentReference}
                    helperText={errors.paymentReference?.message || 'Transaction ID or receipt number'}
                    fullWidth
                  />
                )}
              />

              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes (optional)"
                    multiline
                    rows={2}
                    fullWidth
                  />
                )}
              />

              {paymentMethod === 'MPESA' && (
                <Alert severity="info">
                  Click submit to send M-Pesa STK Push to customer's phone
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<PaymentIcon />}
                disabled={recordPaymentMutation.isPending || mpesaSTKPushMutation.isPending}
                sx={{ mt: 2 }}
              >
                {recordPaymentMutation.isPending || mpesaSTKPushMutation.isPending ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : paymentMethod === 'MPESA' ? (
                  'Send M-Pesa Request'
                ) : (
                  'Record Payment'
                )}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Payments;
